import { useState, useMemo, useEffect } from "react";
import { useAuth } from "@/hooks/use-auth";
import { Layout } from "@/components/layout";
import {
  useUpcomingPayments,
  useUpdateTerms,
  useCreateNote,
  type UpcomingPaymentInvoice,
  type UpcomingPaymentNote,
  type UpcomingPaymentTerms
} from "@/hooks/use-upcoming-payments";
import { hasGroupAdmin } from "@/lib/pagePermissions";
import { Loader2, AlertCircle, ChevronDown, ChevronRight, Settings, Plus, Search, MessageSquare, AlertTriangle, Building2, Calendar, FileText, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";

function formatMoney(amount: number, currency: string = "GBP"): string {
  try {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${currency} ${amount.toFixed(2)}`;
  }
}

function formatDate(dateString: string | null) {
  if (!dateString) return "—";
  try {
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return "—";
    return new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", year: "numeric" }).format(d);
  } catch {
    return "—";
  }
}

function formatMonth(dateString: string | null) {
  if (!dateString) return "Action Required";
  try {
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return "Action Required";
    return new Intl.DateTimeFormat("en-GB", { month: "long", year: "numeric" }).format(d);
  } catch {
    return "Action Required";
  }
}

function getMonthKey(dateString: string | null) {
  if (!dateString) return "ACTION_REQUIRED";
  const d = new Date(dateString);
  if (isNaN(d.getTime())) return "ACTION_REQUIRED";
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

type SupplierGroup = {
  account: string;
  name: string;
  country: string | null;
  totalGbp: number;
  invoices: UpcomingPaymentInvoice[];
};

type MonthGroup = {
  monthKey: string;
  monthLabel: string;
  totalGbp: number;
  suppliers: Record<string, SupplierGroup>;
  isActionRequired: boolean;
};

export default function UpcomingPaymentsPage() {
  const { user } = useAuth();
  const isAdmin = hasGroupAdmin(user, "expenses");
  const { data, isLoading, isFetching, error, refetch } = useUpcomingPayments();
  const [search, setSearch] = useState("");
  const updateTerms = useUpdateTerms();
  const createNote = useCreateNote();

  const [expandedMonths, setExpandedMonths] = useState<Record<string, boolean>>({});
  const [expandedSuppliers, setExpandedSuppliers] = useState<Record<string, boolean>>({});
  const [isTermsDialogOpen, setIsTermsDialogOpen] = useState(false);

  const toggleMonth = (key: string) => setExpandedMonths((prev) => ({ ...prev, [key]: !prev[key] }));
  const toggleSupplier = (key: string) => setExpandedSuppliers((prev) => ({ ...prev, [key]: !prev[key] }));

  // Initialize expanded state once data loads
  useEffect(() => {
    if (data && Object.keys(expandedMonths).length === 0) {
      const initialsM: Record<string, boolean> = {};
      const initialsS: Record<string, boolean> = {};
      // default open the first few months
      const keys = Array.from(new Set(data.invoices.map((inv) => getMonthKey(inv.paymentDate))));
      keys.forEach((k, i) => {
        initialsM[k] = i < 2; // open first two months
      });
      setExpandedMonths(initialsM);
      setExpandedSuppliers(initialsS);
    }
  }, [data, expandedMonths]);

  const { monthGroups, totalGbp } = useMemo(() => {
    if (!data) return { monthGroups: [], totalGbp: 0 };
    let grandTotal = 0;
    const q = search.toLowerCase();

    const filtered = data.invoices.filter((inv) => {
      if (!q) return true;
      return (
        inv.invoice.toLowerCase().includes(q) ||
        inv.supplier.name.toLowerCase().includes(q) ||
        inv.supplier.account.toLowerCase().includes(q) ||
        (inv.purchaseOrders && inv.purchaseOrders.some((po) => String(po.poNumber || "").toLowerCase().includes(q)))
      );
    });

    const groups: Record<string, MonthGroup> = {};
    filtered.forEach((inv) => {
      const mKey = getMonthKey(inv.paymentDate);
      const isAction = mKey === "ACTION_REQUIRED";
      if (!groups[mKey]) {
        groups[mKey] = {
          monthKey: mKey,
          monthLabel: formatMonth(inv.paymentDate),
          totalGbp: 0,
          suppliers: {},
          isActionRequired: isAction,
        };
      }
      
      const sKey = `${mKey}-${inv.supplier.account}`;
      if (!groups[mKey].suppliers[sKey]) {
        groups[mKey].suppliers[sKey] = {
          account: inv.supplier.account,
          name: inv.supplier.name,
          country: inv.supplier.country,
          totalGbp: 0,
          invoices: [],
        };
      }
      
      groups[mKey].suppliers[sKey].invoices.push(inv);
      const valueGbp = inv.invoiceValueGbp ?? 0;
      groups[mKey].suppliers[sKey].totalGbp += valueGbp;
      groups[mKey].totalGbp += valueGbp;
      grandTotal += valueGbp;
    });

    const sortedGroups = Object.values(groups).sort((a, b) => {
      if (a.isActionRequired) return -1;
      if (b.isActionRequired) return 1;
      return a.monthKey.localeCompare(b.monthKey);
    });

    return { monthGroups: sortedGroups, totalGbp: grandTotal };
  }, [data, search]);

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="flex items-center gap-2 text-2xl font-bold text-duffells-grey">
              <Calendar className="h-6 w-6 text-primary" />
              Upcoming Payments
            </h1>
            <p className="mt-1 text-sm text-duffells-grey/60">
              Review future overseas payments grouped by month and supplier.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button 
              variant="outline" 
              onClick={() => refetch()}
              disabled={isLoading || isFetching}
              className="flex items-center gap-2"
            >
              <RefreshCw className={cn("w-4 h-4", (isLoading || isFetching) && "animate-spin")} />
              Refresh
            </Button>
            {isAdmin && (
              <Button variant="outline" onClick={() => setIsTermsDialogOpen(true)} className="flex items-center gap-2">
                <Settings className="w-4 h-4" />
                Manage Terms
              </Button>
            )}
          </div>
        </div>

        {isLoading && !data ? (
          <div className="flex min-h-[400px] items-center justify-center rounded-2xl border border-slate-200 bg-white">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : error && !data ? (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-red-800 flex items-start gap-3">
            <AlertCircle className="w-6 h-6 shrink-0" />
            <div>
              <h3 className="font-semibold text-lg">Unable to load upcoming payments</h3>
              <p className="text-sm mt-1">{(error as Error).message}</p>
              <Button variant="outline" className="mt-4" onClick={() => refetch()} disabled={isFetching}>
                <RefreshCw className={cn("mr-2 h-4 w-4", isFetching && "animate-spin")} />
                Try again
              </Button>
            </div>
          </div>
        ) : data && (
          <div className="space-y-6">
            {/* Summary & Search */}
            <div className="flex flex-wrap gap-4 items-center justify-between bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
              <div className="relative w-full max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input 
                  value={search} 
                  onChange={(e) => setSearch(e.target.value)} 
                  placeholder="Search supplier, invoice or PO..." 
                  className="pl-9 bg-slate-50 border-slate-200" 
                />
              </div>
              <div className="text-right">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Total Projected</p>
                <p className="text-xl font-bold text-slate-900">{formatMoney(totalGbp, "GBP")}</p>
              </div>
            </div>

            {/* List */}
            {monthGroups.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
                <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <h3 className="text-lg font-medium text-slate-900">No payments found</h3>
                <p className="text-sm text-slate-500 mt-1">No upcoming payments match your filters.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {monthGroups.map((month) => {
                  const mKey = month.monthKey;
                  const isExpandedM = expandedMonths[mKey];
                  const sortedSuppliers = Object.values(month.suppliers).sort((a, b) => b.totalGbp - a.totalGbp);
                  
                  return (
                    <div key={mKey} className={cn("rounded-xl border overflow-hidden transition-colors shadow-sm", month.isActionRequired ? "border-amber-200 bg-amber-50/30" : "border-slate-200 bg-white")}>
                      {/* Month Header */}
                      <button 
                        onClick={() => toggleMonth(mKey)}
                        className={cn("w-full flex items-center justify-between p-4 text-left transition-colors hover:bg-slate-50/50", month.isActionRequired ? "bg-amber-100/50 hover:bg-amber-100/80" : "bg-slate-50 hover:bg-slate-100")}
                      >
                        <div className="flex items-center gap-3">
                          <div className={cn("p-1.5 rounded-md", month.isActionRequired ? "bg-amber-200 text-amber-700" : "bg-primary/10 text-primary")}>
                            {isExpandedM ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                          </div>
                          <div>
                            <h2 className={cn("font-bold text-lg flex items-center gap-2", month.isActionRequired ? "text-amber-900" : "text-slate-900")}>
                              {month.monthLabel}
                              {month.isActionRequired && <AlertTriangle className="w-4 h-4 text-amber-600" />}
                            </h2>
                            <p className="text-sm text-slate-500">{Object.keys(month.suppliers).length} suppliers</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-lg text-slate-900">{formatMoney(month.totalGbp, "GBP")}</p>
                        </div>
                      </button>

                      {/* Month Content */}
                      {isExpandedM && (
                        <div className="border-t border-slate-100">
                          {sortedSuppliers.map((supplier) => {
                            const sKey = `${mKey}-${supplier.account}`;
                            const isExpandedS = expandedSuppliers[sKey];
                            
                            return (
                              <div key={sKey} className="border-b border-slate-100 last:border-b-0">
                                {/* Supplier Header */}
                                <button 
                                  onClick={() => toggleSupplier(sKey)}
                                  className="w-full flex items-center justify-between p-3 pl-12 text-left hover:bg-slate-50 transition-colors"
                                >
                                  <div className="flex items-center gap-3">
                                    {isExpandedS ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                                    <Building2 className="w-4 h-4 text-slate-400" />
                                    <div>
                                      <p className="font-semibold text-slate-900">{supplier.name}</p>
                                      <p className="text-xs text-slate-500">
                                        {supplier.account} {supplier.country ? `· ${supplier.country}` : ""}
                                      </p>
                                    </div>
                                  </div>
                                  <div className="text-right">
                                    <p className="font-semibold text-slate-700">{formatMoney(supplier.totalGbp, "GBP")}</p>
                                    <p className="text-xs text-slate-500">{supplier.invoices.length} invoices</p>
                                  </div>
                                </button>
                                
                                {/* Invoices Table */}
                                {isExpandedS && (
                                  <div className="bg-slate-50/50 p-4 pl-[4.5rem] border-t border-slate-50">
                                    <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
                                      <table className="w-full text-sm text-left">
                                        <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500">
                                          <tr>
                                            <th className="px-4 py-3 font-medium">Invoice</th>
                                            <th className="px-4 py-3 font-medium">Date / Terms</th>
                                            <th className="px-4 py-3 font-medium">Stage</th>
                                            <th className="px-4 py-3 font-medium">Expected Delivery</th>
                                            <th className="px-4 py-3 font-medium text-right">Value (Local)</th>
                                            <th className="px-4 py-3 font-medium text-right">Value (GBP)</th>
                                            <th className="px-4 py-3 font-medium text-right">Notes</th>
                                          </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100">
                                          {supplier.invoices.map((inv) => {
                                            const invoiceNotes = data.notes.filter((n) => n.paymentKey === inv.paymentKey);
                                            
                                            return (
                                              <tr key={inv.paymentKey} className="hover:bg-slate-50/50">
                                                <td className="px-4 py-3 align-top">
                                                  <p className="font-medium text-slate-900">{inv.invoice}</p>
                                                  <p className="text-xs text-slate-500 mt-0.5">{formatDate(inv.invoiceDate)}</p>
                                                   {inv.purchaseOrders && inv.purchaseOrders.length > 0 && (
                                                     <p className="text-[10px] text-slate-400 mt-1 max-w-[160px] truncate" title={inv.purchaseOrders.map((po) => po.poNumber).filter(Boolean).join(", ")}>
                                                       PO: {inv.purchaseOrders.map((po) => po.poNumber).filter(Boolean).join(", ")}
                                                    </p>
                                                  )}
                                                </td>
                                                <td className="px-4 py-3 align-top">
                                                  <div className="flex flex-col gap-1">
                                                    <div className="flex items-center gap-1.5 flex-wrap">
                                                      {inv.termsCodes.map((tc) => (
                                                        <Badge key={tc} variant="secondary" className="text-[10px] font-mono px-1.5 py-0 bg-slate-100 text-slate-600">{tc}</Badge>
                                                      ))}
                                                    </div>
                                                    {inv.paymentDateIssue ? (
                                                      <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-50 px-2 py-0.5 rounded-md mt-1 border border-amber-200">
                                                        <AlertTriangle className="w-3 h-3" />
                                                        {inv.paymentDateIssue}
                                                      </span>
                                                    ) : inv.paymentDate ? (
                                                      <span className="text-xs font-medium text-slate-600 mt-1 flex items-center gap-1">
                                                        <Calendar className="w-3 h-3" />
                                                        {formatDate(inv.paymentDate)}
                                                      </span>
                                                    ) : (
                                                      <span className="inline-flex items-center gap-1 text-xs font-medium text-red-600 bg-red-50 px-2 py-0.5 rounded-md mt-1 border border-red-200">
                                                        <AlertCircle className="w-3 h-3" />
                                                        Missing payment date
                                                      </span>
                                                    )}
                                                  </div>
                                                </td>
                                                <td className="px-4 py-3 align-top">
                                                  <Badge variant="outline" className="text-xs font-medium text-slate-600 bg-white border-slate-200">
                                                    Stage {inv.stage.number}: {inv.stage.code}
                                                  </Badge>
                                                </td>
                                                <td className="px-4 py-3 align-top">
                                                  {inv.expectedDeliveryDate ? (
                                                    <div>
                                                      <p className="text-sm text-slate-700">{formatDate(inv.expectedDeliveryDate)}</p>
                                                      {inv.expectedDeliverySource && (
                                                        <p className="text-[10px] text-slate-500 mt-0.5 uppercase tracking-wider">{inv.expectedDeliverySource}</p>
                                                      )}
                                                    </div>
                                                  ) : (
                                                    <span className="text-slate-400">—</span>
                                                  )}
                                                </td>
                                                <td className="px-4 py-3 align-top text-right whitespace-nowrap">
                                                  <span className="font-medium text-slate-700">{formatMoney(inv.invoiceValue, inv.currency)}</span>
                                                </td>
                                                <td className="px-4 py-3 align-top text-right whitespace-nowrap">
                                                   <span className="font-bold text-slate-900">{inv.invoiceValueGbp == null ? "—" : formatMoney(inv.invoiceValueGbp, "GBP")}</span>
                                                </td>
                                                <td className="px-4 py-3 align-top text-right">
                                                  <NotePopover 
                                                    paymentKey={inv.paymentKey} 
                                                    notes={invoiceNotes} 
                                                    onCreateNote={createNote.mutate} 
                                                    isCreating={createNote.isPending} 
                                                  />
                                                </td>
                                              </tr>
                                            );
                                          })}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {isAdmin && data && (
        <TermsManagerDialog 
          open={isTermsDialogOpen} 
          onOpenChange={setIsTermsDialogOpen} 
          terms={data.terms} 
          updateTerms={updateTerms.mutate} 
          isUpdating={updateTerms.isPending} 
        />
      )}
    </Layout>
  );
}

function NotePopover({ paymentKey, notes, onCreateNote, isCreating }: {
  paymentKey: string;
  notes: UpcomingPaymentNote[];
  onCreateNote: (v: { paymentKey: string, body: string }) => void;
  isCreating: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [newNote, setNewNote] = useState("");

  const handleAdd = () => {
    if (!newNote.trim()) return;
    onCreateNote({ paymentKey, body: newNote });
    setNewNote("");
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className="h-8 px-2 text-slate-500 hover:text-slate-900 data-[state=open]:bg-slate-100">
          <MessageSquare className="w-4 h-4 mr-1.5" />
          <span className={notes.length > 0 ? "font-bold text-primary" : ""}>{notes.length || "Add"}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0 shadow-lg border-slate-200">
        <div className="p-3 border-b border-slate-100 bg-slate-50/50">
          <h4 className="font-semibold text-sm text-slate-900">Payment Notes</h4>
        </div>
        <ScrollArea className="max-h-64">
          {notes.length === 0 ? (
            <div className="p-4 text-center text-sm text-slate-500">No notes yet.</div>
          ) : (
            <div className="p-3 space-y-3">
              {notes.map(note => (
                 <div key={note.id} className="bg-slate-50 p-2.5 rounded-lg border border-slate-100">
                   <p className="text-xs text-slate-800 break-words">{note.body}</p>
                  <div className="mt-2 flex items-center justify-between text-[10px] text-slate-500">
                     <span>{note.createdByName || "Unknown user"}</span>
                    <span>{formatDate(note.createdAt)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
        <div className="p-3 border-t border-slate-100 bg-white">
          <div className="flex gap-2">
            <Input 
              value={newNote} 
              onChange={e => setNewNote(e.target.value)} 
              placeholder="Add a note..." 
              className="h-8 text-sm"
              onKeyDown={e => {
                if (e.key === 'Enter') handleAdd();
              }}
            />
            <Button size="sm" className="h-8 shrink-0" onClick={handleAdd} disabled={isCreating || !newNote.trim()}>
              {isCreating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function TermsManagerDialog({ open, onOpenChange, terms, updateTerms, isUpdating }: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  terms: UpcomingPaymentTerms[];
  updateTerms: (v: { termsCode: string, businessDays: number | null }, opts?: { onSuccess?: () => void }) => void;
  isUpdating: boolean;
}) {
  const [editingCode, setEditingCode] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<string>("");

  const handleEdit = (t: UpcomingPaymentTerms) => {
    setEditingCode(t.termsCode);
    setEditValue(t.businessDays !== null ? String(t.businessDays) : "");
  };

  const handleSave = () => {
    if (!editingCode) return;
    const days = parseInt(editValue, 10);
    if (!Number.isInteger(days) || days < 0 || days > 365) return;
    
    updateTerms({ termsCode: editingCode, businessDays: days }, {
      onSuccess: () => setEditingCode(null)
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Manage Payment Terms</DialogTitle>
          <DialogDescription>
            Configure business-day offsets for terms codes. These are used to calculate the payment date from the expected delivery date.
          </DialogDescription>
        </DialogHeader>
        <ScrollArea className="max-h-[60vh] mt-2 border rounded-md">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b sticky top-0 z-10">
              <tr>
                <th className="text-left py-2 px-3 font-medium text-slate-500">Terms Code</th>
                <th className="text-left py-2 px-3 font-medium text-slate-500">Business Days</th>
                <th className="text-right py-2 px-3 font-medium text-slate-500">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {terms.map(t => (
                <tr key={t.termsCode} className="hover:bg-slate-50/50">
                  <td className="py-2 px-3 font-mono text-xs">{t.termsCode}</td>
                  <td className="py-2 px-3">
                    {editingCode === t.termsCode ? (
                      <Input 
                        autoFocus
                        size={5}
                        className="h-7 w-20 px-2 py-1 text-sm"
                        value={editValue}
                        onChange={e => setEditValue(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') handleSave();
                          if (e.key === 'Escape') setEditingCode(null);
                        }}
                      />
                    ) : (
                      <span className={t.businessDays === null ? "text-amber-600 font-medium" : "text-slate-900"}>
                        {t.businessDays !== null ? t.businessDays : "Missing"}
                      </span>
                    )}
                  </td>
                  <td className="py-2 px-3 text-right">
                    {editingCode === t.termsCode ? (
                      <div className="flex justify-end gap-2">
                        <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setEditingCode(null)}>Cancel</Button>
                        <Button size="sm" className="h-7 px-2 text-xs" onClick={handleSave} disabled={isUpdating}>Save</Button>
                      </div>
                    ) : (
                      <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => handleEdit(t)}>Edit</Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </ScrollArea>
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
